import { Box, Flex, Icon, Text } from '@fullstory/components';
import { LabelGroup, RetentionTooltipData, SegmentDot, TimeDisplay } from '@fullstory/library-ui';
import { sortBy } from 'lodash';
import React, { ComponentProps } from 'react';
import { Point } from '../../util/types';
import { ChartingShape } from '../ChartingShape';
import { Line } from '../Line';
import css from './trendsTooltip.module.scss';
import { getTooltipDisplayValue } from './utils';
import { PartialPeriodWarning } from './PartialPeriodWarning';

export const testIds = {
  CONTAINER: 'trends-tooltip-container',
  TIME_DISPLAY: 'trends-tooltip-time-display',
  PARTIAL_PERIOD_WARNING_ICON_TABLE_ROW: 'partial-period-warning-icon-table-row',
};

export interface TooltipPoint extends Point, Pick<LabelGroup, 'label' | 'pillLabel'> {
  /* if color and shape are provided then this is not used */
  fill: ComponentProps<typeof Line>['color'];
  shape?: ComponentProps<typeof ChartingShape>['shape'];
  color?: ComponentProps<typeof ChartingShape>['color'];
  outline?: boolean;
  /* if provided, the shape will be filled with a gradient instead of the provided color */
  gradient?: boolean;
}

export interface TrendsTooltipProps {
  data: TooltipPoint[];
  legendColor?: ComponentProps<typeof SegmentDot>['color'];
  retentionTooltip?: RetentionTooltipData;
  alwaysShowTime?: boolean;
  showPartialPeriods?: boolean;
}

export const TrendsTooltip: React.FC<TrendsTooltipProps> = ({
  data,
  legendColor,
  retentionTooltip,
  alwaysShowTime,
  showPartialPeriods,
}) => (
  <Box data-component="TrendsTooltip" padding={[8, 12]} testId={testIds.CONTAINER}>
    {data?.[0] && (
      <Box paddingBottom={8} testId={testIds.TIME_DISPLAY}>
        <Text appearance="bodySmall" color="default-inverse">
          <TimeDisplay
            startDate={data[0].displayStartDate ?? data[0].startDate}
            endDate={data[0].displayEndDate ?? data[0].endDate}
            alwaysShowTime={alwaysShowTime}
          />
        </Text>
      </Box>
    )}
    {/** Using a table to allow the numbers to be right aligned for the numbers */}
    <table>
      <tbody>
        {sortBy(data, d => d.value)
          .reverse()
          .map(point => (
            <TooltipTable
              point={point}
              retentionTooltip={retentionTooltip}
              legendColor={legendColor}
              key={point.label}
              showPartialPeriods={showPartialPeriods && data.length > 1}
            />
          ))}
      </tbody>
    </table>
    {showPartialPeriods
      ? data.some(d => d.isPartialPeriod) && (
          <PartialPeriodWarning className={css.tooltipPartialPeriod} padding={[4, 0]} />
        )
      : null}
  </Box>
);

interface TooltipTableProps {
  retentionTooltip?: RetentionTooltipData;
  point: TooltipPoint;
  legendColor?: ComponentProps<typeof SegmentDot>['color'];
  showPartialPeriods?: boolean;
}

function TooltipTable({
  retentionTooltip,
  point,
  legendColor,
  showPartialPeriods,
}: TooltipTableProps) {
  if (!!retentionTooltip && point.startDate < new Date(retentionTooltip.retentionTimestamp)) {
    return (
      <tr key={`${point.fill}-${point.startDate}`}>
        <Box paddingRight={8} width="300px">
          <Text color="default-inverse" appearance="bodySmall">
            {retentionTooltip.text}
          </Text>
        </Box>
      </tr>
    );
  }

  return (
    <tr data-component="TooltipTable" key={`${point.fill}-${point.startDate}`}>
      <td>
        {point.shape && point.color ? (
          <Box paddingRight={12}>
            <Flex justifyContent="center" alignItems="center">
              <ChartingShape shape={point.shape} color={point.color} gradient={point?.gradient} />
            </Flex>
          </Box>
        ) : (
          <SegmentDot
            color={legendColor || point.fill}
            variant={point.outline ? 'outlined' : 'primary'}
          />
        )}
      </td>
      <td className={css.trendsTooltipValues}>
        <Box paddingRight={8} word-break="break-all">
          <Text align="right" color="default-inverse" appearance="bodySmall">
            {getTooltipDisplayValue(point)}
          </Text>
        </Box>
      </td>
      <td className={css.tooltipLabel}>
        {point.pillLabel?.value ? (
          <Box className={css.tooltipPill}>
            <Text color="default-inverse" appearance="bodySmall" noWrap>
              {point.pillLabel.value}
            </Text>
          </Box>
        ) : (
          <Box word-break="break-all">
            <Text color="default-inverse" appearance="bodySmall" noWrap>
              {point.label}
            </Text>
          </Box>
        )}
      </td>
      {showPartialPeriods && point.isPartialPeriod ? (
        <td>
          <Icon
            size={16}
            testId={testIds.PARTIAL_PERIOD_WARNING_ICON_TABLE_ROW}
            name="warning-fill"
            color="warning"
          />
        </td>
      ) : null}
    </tr>
  );
}
